import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  SearchIcon,
  ViewIcon,
  WarningIcon,
} from "@chakra-ui/icons";
import { FaChargingStation, FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { FaTowerBroadcast } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import useAxios from "axios-hooks";
import { motion } from "framer-motion";
import LoadingSpinner from "components/feedback/LoadingSpinner";
import { GetFacilityDetails } from "utils/facilities";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const EmptyStateIllustration = ({ onGoToLocations }) => (
  <MotionFlex
    direction="column"
    align="center"
    justify="center"
    py={12}
    px={6}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Icon as={FaChargingStation} boxSize={16} color="brand.500" mb={4} />
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      No Private Chargers Available
    </Text>
    <Text color="gray.500" textAlign="center" maxW="lg" mb={4}>
      There are no private charging locations configured yet. To set up a
      private charger, follow the steps below:
    </Text>
    <VStack
      align="start"
      spacing={2}
      bg="gray.50"
      borderRadius="lg"
      p={5}
      maxW="lg"
      w="100%"
      mb={5}
    >
      <HStack align="start" spacing={3}>
        <Badge colorScheme="brand" borderRadius="full" px={2} minW="24px" textAlign="center">1</Badge>
        <Text fontSize="sm" color="gray.600">
          Go to the <Text as="span" fontWeight="bold">Locations</Text> page.
        </Text>
      </HStack>
      <HStack align="start" spacing={3}>
        <Badge colorScheme="brand" borderRadius="full" px={2} minW="24px" textAlign="center">2</Badge>
        <Text fontSize="sm" color="gray.600">
          Select a location and navigate to the <Text as="span" fontWeight="bold">Roaming</Text> tab.
        </Text>
      </HStack>
      <HStack align="start" spacing={3}>
        <Badge colorScheme="brand" borderRadius="full" px={2} minW="24px" textAlign="center">3</Badge>
        <Text fontSize="sm" color="gray.600">
          Click on <Text as="span" fontWeight="bold">Enable Private Charging</Text> and save the configuration.
        </Text>
      </HStack>
    </VStack>
    <Button
      colorScheme="brand"
      bg="brand.500"
      color="white"
      _hover={{ bg: "#e06b2b" }}
      size="md"
      borderRadius="md"
      leftIcon={<Icon as={ViewIcon} />}
      onClick={onGoToLocations}
    >
      Go to Locations
    </Button>
  </MotionFlex>
);

const StatusBadge = ({ status }) => {
  let color, icon, label;

  switch (status.toLowerCase()) {
    case "active":
      color = "green";
      icon = CheckCircleIcon;
      label = "Active";
      break;
    case "inactive":
      color = "red";
      icon = WarningIcon;
      label = "Inactive";
      break;
    default:
      color = "green";
      icon = CheckCircleIcon;
      label = "Active";
  }

  return (
    <Badge
      display="flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
    >
      <Icon as={icon} mr={1} boxSize={3} />
      <Text fontSize="xs" fontWeight="medium">
        {label}
      </Text>
    </Badge>
  );
};

const LocationAvatar = ({ name }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <Avatar
      name={name}
      size="sm"
      bg="brand.500"
      color="white"
      fontWeight="bold"
      getInitials={() => initials}
    />
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "brand" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

const tableColumns = [
  { Header: "S.No" },
  { Header: "Name" },
  { Header: "PinCode" },
  { Header: "Amenities" },
  { Header: "Roaming" },
  { Header: "Actions" },
];

const PrivateLocations = ({ id }) => {
  const [privateLocations, setPrivateLocations] = useState([]);
  const [groupLocations, setGroupLocations] = useState([]);
  const [updatingLocationId, setUpdatingLocationId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [accessFilter, setAccessFilter] = useState("all");
  const toast = useToast();
  const navigate = useNavigate();

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const themeColor = "brand.500";
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const [{ loading: privateLoading, error: privateError }, fetchPrivate] = useAxios(
    { url: "/api/locations/private", method: "GET" },
    { manual: true }
  );
  const [{ loading: groupLoading, error: groupError }, fetchGroup] = useAxios(
    { url: `/api/userprofiles/${id}/locations`, method: "GET" },
    { manual: true }
  );
  const [{ loading: addLoading }, addGroupLocations] = useAxios(
    { url: `/api/userprofiles/${id}/locations`, method: "POST" },
    { manual: true }
  );
  const [{ loading: removeLoading }, removeGroupLocations] = useAxios(
    { url: `/api/userprofiles/${id}/locations`, method: "DELETE" },
    { manual: true }
  );

  const fetchGroupLocations = useCallback(async () => {
    const res = await fetchGroup();
    setGroupLocations(res?.data?.data || []);
  }, [fetchGroup]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [privateRes, groupRes] = await Promise.all([
        fetchPrivate(),
        fetchGroup(),
      ]);
      console.log("Private location list:", privateRes?.data?.data || []);
      setPrivateLocations(privateRes?.data?.data || []);
      setGroupLocations(groupRes?.data?.data || []);
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return;
      console.error("Error fetching private location access data:", err);
    }
  }, [fetchPrivate, fetchGroup]);

  useEffect(() => {
    if (id) {
      fetchInitialData();
    }
  }, [id, fetchInitialData]);

  const accessibleLocationIds = useMemo(
    () => new Set(groupLocations?.map((item) => Number(item?.LocationId))),
    [groupLocations]
  );

  const columns = useMemo(() => tableColumns, []);
  const filteredLocations = useMemo(() => {
    if (accessFilter === "allowed") {
      return privateLocations.filter((loc) =>
        accessibleLocationIds.has(Number(loc?.Id))
      );
    }
    if (accessFilter === "not_allowed") {
      return privateLocations.filter(
        (loc) => !accessibleLocationIds.has(Number(loc?.Id))
      );
    }
    return privateLocations;
  }, [privateLocations, accessibleLocationIds, accessFilter]);
  const data = useMemo(() => filteredLocations, [filteredLocations]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") {
          return rows;
        }

        const lowercasedQuery = query.toLowerCase().trim();

        return rows.filter((row) => {
          const name = row.original.Name || "";
          const city = row.original.City || "";
          const state = row.original.State || "";
          const postalCode = row.original.PostalCode || "";
          const address = row.original.Address || "";

          return (
            name.toLowerCase().includes(lowercasedQuery) ||
            city.toLowerCase().includes(lowercasedQuery) ||
            state.toLowerCase().includes(lowercasedQuery) ||
            postalCode.toLowerCase().includes(lowercasedQuery) ||
            address.toLowerCase().includes(lowercasedQuery)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
  } = tableInstance;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setGlobalFilter(value);
  };

  const isMutating = addLoading || removeLoading;

  const handleAddAccess = async (locationId) => {
    try {
      setUpdatingLocationId(locationId);
      await addGroupLocations({ data: { location_ids: [locationId] } });
      await fetchGroupLocations();
      toast({
        title: "Private location access added",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return;
      toast({
        title: "Unable to add access",
        description: err?.response?.data?.error || "Please try again",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingLocationId(null);
    }
  };

  const handleRemoveAccess = async (locationId) => {
    try {
      setUpdatingLocationId(locationId);
      await removeGroupLocations({ data: { location_ids: [locationId] } });
      await fetchGroupLocations();
      toast({
        title: "Private location access removed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return;
      toast({
        title: "Unable to remove access",
        description: err?.response?.data?.error || "Please try again",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingLocationId(null);
    }
  };

  const loading = privateLoading || groupLoading;

  return loading ? (
    <LoadingSpinner />
  ) : privateLocations.length > 0 ? (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
    >
      <GlassMorphismCard p={0}>
        {/* Header with search */}
        <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <VStack
              align="start"
              spacing={2}
              width={{ base: "100%", md: "auto" }}
            >
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search Locations
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name, UID, city, state, postal code..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
              <HStack spacing={2} mt={2}>
                <Button
                  size="xs"
                  variant={accessFilter === "all" ? "solid" : "outline"}
                  colorScheme={accessFilter === "all" ? "brand" : "gray"}
                  borderRadius="full"
                  onClick={() => setAccessFilter("all")}
                >
                  All ({privateLocations.length})
                </Button>
                <Button
                  size="xs"
                  variant={accessFilter === "allowed" ? "solid" : "outline"}
                  colorScheme={accessFilter === "allowed" ? "green" : "gray"}
                  borderRadius="full"
                  onClick={() => setAccessFilter("allowed")}
                >
                  Allowed (
                  {privateLocations.filter((loc) =>
                    accessibleLocationIds.has(Number(loc?.Id))
                  ).length}
                  )
                </Button>
                <Button
                  size="xs"
                  variant={accessFilter === "not_allowed" ? "solid" : "outline"}
                  colorScheme={accessFilter === "not_allowed" ? "red" : "gray"}
                  borderRadius="full"
                  onClick={() => setAccessFilter("not_allowed")}
                >
                  Not Allowed (
                  {privateLocations.filter(
                    (loc) => !accessibleLocationIds.has(Number(loc?.Id))
                  ).length}
                  )
                </Button>
              </HStack>
            </VStack>

            <Flex align="center" justify="flex-end">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Private Locations
              </Text>
              <Badge ml={2} colorScheme="brand" borderRadius="full" px={2}>
                {privateLocations.length} total
              </Badge>
            </Flex>
          </Flex>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table {...getTableProps()} variant="simple" colorScheme="gray">
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      color="gray.500"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ color: themeColor }}
                      transition="all 0.2s"
                    >
                      <Flex justify="center" align="center" gap={1}>
                        {column.render("Header")}
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon as={FaSortDown} />
                            ) : (
                              <Icon as={FaSortUp} />
                            )
                          ) : (
                            <Icon as={FaSort} opacity={0.3} />
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                const isAccessible = accessibleLocationIds.has(
                  Number(row.original.Id)
                );
                return (
                  <MotionTr
                    {...row.getRowProps()}
                    key={index}
                    whileHover={{ backgroundColor: hoverBg }}
                    transition={{ duration: 0.2 }}
                    _hover={{ bg: hoverBg }}
                    cursor="pointer"
                  >
                    {row.cells.map((cell, cellIndex) => {
                      let cellData = "";
                      if (cell.column.Header === "S.No") {
                        cellData = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {pageIndex * pageSize + Number(cell.row.id) + 1}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Name") {
                        cellData = (
                          <Flex align="center" gap={3}>
                            <LocationAvatar
                              name={cell.row.original.Name || "NA"}
                            />
                            <Box>
                              <Text color={textColor} fontWeight="semibold">
                                {cell.row.original.Name}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {cell.row.original.City},{" "}
                                {cell.row.original.State}
                              </Text>
                            </Box>
                          </Flex>
                        );
                      } else if (cell.column.Header === "PinCode") {
                        cellData = (
                          <Flex align="center" justify="center">
                            <Text color={textColor} fontSize="sm">
                              {cell.row.original.PostalCode || "N/A"}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Amenities") {
                        const facilities = cell.row.original.Facilities || [];
                        const top3 = facilities.slice(0, 3);
                        cellData = (
                          <Flex align="center" justify="center" gap={2}>
                            {top3.length > 0 ? (
                              top3.map((facility) => {
                                const details = GetFacilityDetails(facility);
                                return (
                                  <Tooltip
                                    key={facility}
                                    label={details.text}
                                    hasArrow
                                    placement="top"
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      p={2}
                                      borderRadius="full"
                                      bg="rgba(35, 169, 121, 0.08)"
                                      _hover={{
                                        transform: "scale(1.1)",
                                        boxShadow:
                                          "0 4px 12px rgba(35, 169, 121, 0.2)",
                                      }}
                                      transition="all 0.2s ease"
                                    >
                                      <Icon
                                        as={details.icon}
                                        boxSize={4}
                                        color="brand.500"
                                      />
                                    </Box>
                                  </Tooltip>
                                );
                              })
                            ) : (
                              <Text fontSize="xs" color="gray.400">
                                —
                              </Text>
                            )}
                            {facilities.length > 3 && (
                              <Tooltip
                                label={facilities
                                  .slice(3)
                                  .map((f) => GetFacilityDetails(f).text)
                                  .join(", ")}
                                hasArrow
                                placement="top"
                              >
                                <Badge
                                  colorScheme="brand"
                                  borderRadius="full"
                                  fontSize="xs"
                                  cursor="pointer"
                                >
                                  +{facilities.length - 3}
                                </Badge>
                              </Tooltip>
                            )}
                          </Flex>
                        );
                      } else if (cell.column.Header === "Roaming") {
                        const isRoamingEnabled =
                          cell.row.original.IsOcpiEnabled;
                        cellData = (
                          <Flex align="center" justify="center">
                            <Tooltip
                              label={
                                isRoamingEnabled
                                  ? "OCPI Roaming Enabled"
                                  : "OCPI Roaming Disabled"
                              }
                              hasArrow
                              placement="top"
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                p={2}
                                borderRadius="full"
                                bg={
                                  isRoamingEnabled
                                    ? "rgba(35, 169, 121, 0.08)"
                                    : "rgba(160, 174, 192, 0.08)"
                                }
                                cursor="pointer"
                                _hover={{
                                  transform: "scale(1.1)",
                                  boxShadow:
                                    "0 4px 12px rgba(35, 169, 121, 0.2)",
                                }}
                                transition="all 0.2s ease"
                              >
                                <Icon
                                  as={FaTowerBroadcast}
                                  boxSize={4}
                                  color={
                                    isRoamingEnabled ? "brand.500" : "gray.400"
                                  }
                                  transition="all 0.3s ease"
                                />
                              </Box>
                            </Tooltip>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Actions") {
                        cellData = (
                          <Flex align="center" justify="center" gap={2}>
                            <Tooltip
                              label="View Details"
                              placement="top"
                              hasArrow
                            >
                              <IconButton
                                icon={<ViewIcon />}
                                colorScheme="blue"
                                variant="ghost"
                                size="sm"
                                borderRadius="full"
                                aria-label="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/locations/view/${cell.row.original.Id}/overview`
                                  );
                                }}
                              />
                            </Tooltip>

                            <Tooltip
                              label={
                                isAccessible
                                  ? "Toggle off to remove access"
                                  : "Toggle on to allow access"
                              }
                              placement="top"
                              hasArrow
                            >
                              <Flex align="center">
                                <Switch
                                  colorScheme="green"
                                  size="md"
                                  isChecked={isAccessible}
                                  isDisabled={
                                    isMutating &&
                                    updatingLocationId ===
                                      cell.row.original.Id
                                  }
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (isAccessible) {
                                      handleRemoveAccess(
                                        cell.row.original.Id
                                      );
                                    } else {
                                      handleAddAccess(
                                        cell.row.original.Id
                                      );
                                    }
                                  }}
                                />
                              </Flex>
                            </Tooltip>
                          </Flex>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={cellIndex}
                          py={4}
                          px={6}
                          borderColor={borderColor}
                          fontSize="sm"
                        >
                          {cellData}
                        </Td>
                      );
                    })}
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {page.length} of {filteredLocations.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={pageIndex === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              <>
                {pageIndex > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {pageIndex > 2 && <Text>...</Text>}

                {pageIndex > 0 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                    {pageIndex}
                  </PaginationButton>
                )}

                <PaginationButton isActive onClick={() => gotoPage(pageIndex)}>
                  {pageIndex + 1}
                </PaginationButton>

                {pageIndex < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                    {pageIndex + 2}
                  </PaginationButton>
                )}

                {pageIndex < pageCount - 3 && <Text>...</Text>}

                {pageIndex < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>
      </GlassMorphismCard>
    </MotionBox>
  ) : (
    <GlassMorphismCard w="100%" py={8}>
      <EmptyStateIllustration onGoToLocations={() => navigate("/admin/locations")} />
    </GlassMorphismCard>
  );
};

export default PrivateLocations;
